// comment out the revenue column for phase1

import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  Avatar,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../SnackBar";
import CustomTooltip from "../CustomTooltip";
import SkeletonTable from "../Skeletons/SkeletonTable";
import CustomTable from "../CustomTable";
import CustomAccordion from "../CustomAccordion";
import SkeletonAccordion from "../Skeletons/SkeletonAccordion";

// Group a supplier's categories by item type for the "Categories in" column.
// Backend contract: supplier.categories_by_type = [{ type, categories: [{ id, name, kind, type }] }]
// (an object keyed by type is also accepted). Fallback for a payload without the
// grouped shape: group the flat `categories` by their `kind` tag, and treat
// untagged ones as services — where they used to come from.
const KIND_TO_TYPE = {
  service: "services",
  product: "products",
  restaurant: "restaurants",
};

const getCategoryGroups = (supplier) => {
  const grouped = supplier?.categories_by_type;
  if (Array.isArray(grouped)) return grouped;
  if (grouped && typeof grouped === "object") {
    return Object.entries(grouped).map(([type, categories]) => ({
      type,
      categories: categories || [],
    }));
  }
  const groups = [];
  (supplier?.categories || []).forEach((c) => {
    const type = c.type || KIND_TO_TYPE[c.kind] || "services";
    let group = groups.find((g) => g.type === type);
    if (!group) groups.push((group = { type, categories: [] }));
    group.categories.push(c);
  });
  return groups;
};

const categoryChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 8px",
  borderRadius: "6px",
  backgroundColor: "#F2F3FB",
  fontSize: "13px",
  fontWeight: 500,
  color: "rgba(62, 63, 76, 0.9)",
  whiteSpace: "nowrap",
  cursor: "default",
};

const MySuppliersCard = ({
  data,
  tabData,
  activeTab,
  sortOrder,
  toggleSortOrder,
  handleSupplierProfileModal,
  supplierStatusChanged,
  isLoading,
  containerRef,
}) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const showSnackbar = useSnackbar();
  const { t } = useTranslation([
    "page-my-suppliers",
    "categories",
    "products_categories",
    "restaurants_categories",
  ]);

  // A supplier's categories span three verticals and each tree is translated in
  // its own namespace, so the name alone isn't enough to look one up — the API
  // tags every category with its `kind`. Older payloads have no kind; those
  // fall back to the services namespace, which is where they used to come from.
  const CATEGORY_NS = {
    service: "categories",
    product: "products_categories",
    restaurant: "restaurants_categories",
  };
  const categoryNames = (categories = []) =>
    categories
      .map((c) => t(c.name, { ns: CATEGORY_NS[c.kind] || "categories" }))
      .join(", ");
  const [expandedId, setExpandedId] = useState(null);

  const formatDate = (dateStr) => (dateStr ? dateStr.replace(/\//g, ".") : "-");

  const getSupplierStatus = (supplierId) => {
    if (tabData === 1) {
      const changed = supplierStatusChanged.find(
        (s) => s.supplierId === Number(supplierId)
      );
      return changed?.status ?? null;
    }
    return null;
  };

  const actionButtonStyle = {
    padding: "6px 8px !important",
    borderRadius: "4px",
    backgroundColor: "#F2F3FB",
    textTransform: "none",
    fontWeight: 500,
    fontSize: 13,
    color: "#248B70",
  };

  const getContactIcons = (supplier) => (
    <Box sx={{ ml: "-8px" }}>
      <CustomTooltip
        title={t("table_body.copy_email_tooltip")}
        tooltipSxStyles={{ paddingY: "5px" }}
      >
        <IconButton
          onClick={() => {
            navigator.clipboard.writeText(supplier.contact.email);
            showSnackbar(`${t("table_body.copy_email")}`, "success");
          }}
        >
          <img src="/icons/mail-forward-black.svg" alt="mail" />
        </IconButton>
      </CustomTooltip>
      <CustomTooltip
        title={t("table_body.copy_phone_tooltip")}
        tooltipSxStyles={{ paddingY: "5px" }}
      >
        <IconButton
          onClick={() => {
            navigator.clipboard.writeText(supplier.contact.phone);
            showSnackbar(`${t("table_body.copy_phone")}`, "success");
          }}
        >
          <img src="/icons/phone-outgoing-black.svg" alt="phone" />
        </IconButton>
      </CustomTooltip>
      <CustomTooltip
        title={t("table_body.whatsapp_tooltip")}
        tooltipSxStyles={{ paddingY: "5px" }}
      >
        <IconButton
          onClick={(e) => {
            e.preventDefault();
            window.open(
              `https://api.whatsapp.com/send/?phone=${supplier.contact.phone}&text=hello`,
              "_blank",
              "noopener,noreferrer"
            );
          }}
        >
          <img src="/icons/whatsapp-icon-black.svg" alt="whatsapp" />
        </IconButton>
      </CustomTooltip>
    </Box>
  );

  const getStatusBadge = (supplier) => {
    const overriddenStatus = getSupplierStatus(supplier.supplier_id);
    const displayStatusRaw = overriddenStatus || supplier.status;
    const displayStatus =
      displayStatusRaw?.charAt(0).toUpperCase() +
      displayStatusRaw?.slice(1).toLowerCase();
    const lowerStatus = displayStatusRaw?.toLowerCase();

    return (
      <Box
        sx={{
          padding: "4px 8px",
          borderRadius: "4px",
          background: "rgba(36, 139, 112, 0.16)",
          color: "#248B70",
          fontSize: "13px",
          fontWeight: 500,
          display: "inline-block",
          ...(lowerStatus?.startsWith("paused") && {
            background: "rgba(255, 151, 51, 0.16)",
            color: "#FF9733",
          }),
        }}
      >
        {t("status." + displayStatus)}
      </Box>
    );
  };

  const getActionButton = (supplier) => (
    <Button
      className="btn-remove"
      sx={actionButtonStyle}
      onClick={() => {
        handleSupplierProfileModal(
          supplier,
          activeTab === 1 ? "view" : activeTab === 2 ? "offer" : "cancel"
        );
      }}
    >
      {activeTab === 1 || activeTab === 3
        ? `${t("actions.view")}`
        : `${t("actions.review")}`}
    </Button>
  );

  const columns = [
    {
      id: "supplier",
      label: t("table_tabs.supplier"),
      render: (supplier) => (
        <Box display="flex" alignItems="center">
          <Avatar
            alt={supplier.business_name}
            src={supplier.display_image_url}
            sx={{ width: 32, height: 32 }}
          />
          <Box
            ml="16px"
            sx={{
              maxWidth: "220px",
            }}
          >
            <CustomTooltip
              title={supplier.supplier_name}
              tooltipSxStyles={{ paddingY: "5px" }}
            >
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "rgba(62, 63, 76, 0.9)",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "max-content",
                }}
              >
                {supplier.supplier_name}
              </Typography>
            </CustomTooltip>

            <Typography
              sx={{
                fontSize: "12px",
                color: "rgba(62, 63, 76, 0.9)",
                fontWeight: 400,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
            >
              {supplier.business_name}
            </Typography>
          </Box>
        </Box>
      ),
      headerStyle: { width: "302px" },
      cellStyle: { width: "302px" },
    },
    {
      id: "location",
      label: t("table_tabs.location"),
      render: (supplier) => (
        <CustomTooltip
          title={supplier.location}
          tooltipSxStyles={{ paddingY: "5px" }}
        >
          {supplier.location.length > 25
            ? supplier.location.slice(0, 25) + "..."
            : supplier.location}
        </CustomTooltip>
      ),
    },
    {
      id: "categories",
      label: t("table_tabs.categories_in"),
      render: (supplier) => {
        // One chip per item type (Services / Products / Restaurants / …), each
        // showing the count and a tooltip listing that type's categories. Types
        // with no categories are hidden. Driven by the data — new item types
        // appear here automatically.
        const groups = getCategoryGroups(supplier).filter(
          (g) => (g.categories?.length || 0) > 0
        );
        if (!groups.length) return "-";
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {groups.map((g) => (
              <CustomTooltip
                key={g.type}
                title={categoryNames(g.categories)}
                tooltipSxStyles={{ paddingY: "5px" }}
              >
                <Box sx={categoryChipStyle}>
                  {t(`item_types.${g.type}`, { defaultValue: g.type })} (
                  {g.categories.length})
                </Box>
              </CustomTooltip>
            ))}
          </Box>
        );
      },
    },
    {
      id: "date",
      label:
        activeTab === 1
          ? t("table_tabs.joined")
          : activeTab === 2
          ? t("table_tabs.offer_from")
          : t("table_tabs.request_from"),
      render: (supplier) =>
        formatDate(
          activeTab === 1
            ? supplier.together_since
            : activeTab === 2
            ? supplier.offer_date
            : supplier.invite_date
        ),
      headerRender: () => (
        <Box
          onClick={toggleSortOrder}
          sx={{
            cursor: "pointer",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
          }}
        >
          {activeTab === 1
            ? t("table_tabs.joined")
            : activeTab === 2
            ? t("table_tabs.offer_from")
            : t("table_tabs.request_from")}
          {sortOrder === "asc" ? (
            <KeyboardArrowDownIcon />
          ) : (
            <KeyboardArrowUpIcon />
          )}
        </Box>
      ),
      headerStyle: { cursor: "pointer", userSelect: "none" },
    },
    // comment out the revenue column for phase1
    // ...(activeTab === 1
    //   ? [
    //       {
    //         id: "revenue",
    //         label: t("table_tabs.revenue"),
    //         render: (supplier) =>
    //           supplier.community_purchases.toLocaleString("en-US", {
    //             style: "currency",
    //             currency: "ILS",
    //           }),
    //       },
    //     ]
    //   : []),
    {
      id: "contact",
      label: t("table_tabs.contact"),
      render: (supplier) => getContactIcons(supplier),
    },
    ...(activeTab === 1
      ? [
          {
            id: "status",
            label: t("table_tabs.status"),
            render: (supplier) => getStatusBadge(supplier),
          },
        ]
      : []),
    {
      id: "actions",
      label: t("table_tabs.actions"),
      render: (supplier) => getActionButton(supplier),
    },
  ];

  const mobileDetails = (supplier) => [
    { label: t("table_tabs.location"), value: supplier.location || "-" },
    {
      label: t("table_tabs.categories_in"),
      value: (() => {
        const groups = getCategoryGroups(supplier).filter(
          (g) => (g.categories?.length || 0) > 0
        );
        return groups.length
          ? groups
              .map(
                (g) =>
                  `${t(`item_types.${g.type}`, { defaultValue: g.type })} (${
                    g.categories.length
                  })`
              )
              .join(", ")
          : "-";
      })(),
    },
    {
      label:
        activeTab === 1
          ? t("table_tabs.joined")
          : activeTab === 2
          ? t("table_tabs.offer_from")
          : t("table_tabs.request_from"),
      value: formatDate(
        activeTab === 1
          ? supplier.together_since
          : activeTab === 2
          ? supplier.offer_date
          : supplier.invite_date
      ),
    },
    { label: t("table_tabs.contact"), value: getContactIcons(supplier) },
    ...(activeTab === 1
      ? [{ label: t("table_tabs.status"), value: getStatusBadge(supplier) }]
      : []),
  ];

  const handleChange = (memberId) => (event, isExpanded) => {
    setExpandedId(isExpanded ? memberId : null);
  };

  return (
    <>
      {isMobile ? (
        isLoading ? (
          <SkeletonAccordion />
        ) : (
          <Box
            ref={containerRef}
            className="custom-scrollbar"
            sx={{ overflowY: "auto", height: "100%", paddingRight: "8px" }}
          >
            {data.map((supplier) => (
              <CustomAccordion
                key={supplier.supplier_id}
                imageUrl={supplier.display_image_url}
                expanded={expandedId === supplier.supplier_id}
                onChange={handleChange(supplier.supplier_id)}
                summaryContent={
                  <Box>
                    <Typography
                      color="rgba(62, 63, 76, 0.9)"
                      sx={{
                        fontSize: "15px",
                        fontWeight: 500,
                      }}
                    >
                      {supplier.supplier_name}
                    </Typography>
                    <Typography
                      color="rgba(62, 63, 76, 0.9)"
                      sx={{
                        fontSize: "12px",
                        fontWeight: 400,
                      }}
                    >
                      {supplier.business_name}
                    </Typography>
                  </Box>
                }
                details={mobileDetails(supplier)}
                actions={getActionButton(supplier)}
              />
            ))}
          </Box>
        )
      ) : isLoading ? (
        <SkeletonTable columns={columns} rowCount={5} />
      ) : (
        <CustomTable
          columns={columns}
          data={data}
          containerRef={containerRef}
        />
      )}
    </>
  );
};

export default MySuppliersCard;
